/**
 * Gemeinsame Zaehlung laufender Klassifizierungen.
 *
 * Vorher rechneten die Dateiseite und die Dokumentenklassen-Seite denselben Banner-Text mit zwei
 * verschiedenen Formeln aus: die Dateiseite zaehlte jede Zeile mit DB-Status PROCESSING (projektweit,
 * inklusive haengengebliebener Altlasten), die Dokumentenklassen-Seite
 * `max(runningSet.size, batch.total - batch.done)`. Beide Zahlen widersprachen sich und der
 * Batch-Anteil kam nach einem Seitenwechsel als Zombie-Wert zurueck.
 *
 * Grundlage ist deshalb ausschliesslich eine ID-Menge: die Backend-Liste `runningClassifications`,
 * vereinigt mit den noch offenen Datei-IDs laufender Reklassifizierungs-Batches und den in dieser
 * Sitzung selbst angestossenen Klassifizierungen.
 */

export interface RunningClassificationSources {
  /** Backend-Wahrheit: StateConfigService.runningClassifications. */
  runningIds: Iterable<string>;
  /** Noch offene Datei-IDs der laufenden Reklassifizierungs-Batches. */
  pendingBatchIds?: Iterable<string>;
  /** Selbst angestossene Klassifizierungen, die das Backend noch nicht meldet. */
  locallyStartedIds?: Iterable<string>;
}

/** Vereinigt die Quellen zu einer ID-Menge. */
export function collectRunningClassificationIds(sources: RunningClassificationSources): Set<string> {
  const ids = new Set<string>();
  for (const id of sources.runningIds ?? []) {
    if (id) ids.add(id);
  }
  for (const id of sources.pendingBatchIds ?? []) {
    if (id) ids.add(id);
  }
  for (const id of sources.locallyStartedIds ?? []) {
    if (id) ids.add(id);
  }
  return ids;
}

/**
 * Zaehlt die laufenden Klassifizierungen.
 *
 * @param sources        die drei ID-Quellen
 * @param knownFileIds   wenn gesetzt, wird auf die Dateien eingeschraenkt, die der Aufrufer kennt.
 *                       Die Dateiseite uebergibt hier ihre Projektdateien; die Dokumentenklassen-Seite
 *                       kennt keine Dateiliste und laesst den Parameter weg.
 * @param excludedIds    als haengengeblieben erkannte Dateien, die nicht mitgezaehlt werden sollen
 */
export function countRunningClassifications(
  sources: RunningClassificationSources,
  knownFileIds?: ReadonlySet<string>,
  excludedIds?: ReadonlySet<string>,
): number {
  const ids = collectRunningClassificationIds(sources);

  let count = 0;
  for (const id of ids) {
    if (excludedIds?.has(id)) continue;
    if (knownFileIds && !knownFileIds.has(id)) continue;
    count++;
  }
  return count;
}
